import { create } from 'zustand';
import { supabase } from '../utils/supabase';
import type { MemberExtends, MembershipStatus } from '../types/member';
import { decrtyptReferralCode } from '../utils/auth/userManagement';

interface MemberState {
  users: MemberExtends[];
  isLoading: boolean;
  loadMembers: () => Promise<void>;
  setMembers: (users: MemberExtends[]) => void;
  updateMember: (id: string, updates: Partial<MemberExtends>) => void;
  deleteMember: (id: string) => void;
  cancelDeletion: (id: string) => void;
}

export const useMemberStore = create<MemberState>((set, get) => {
  return {
    users: [],
    isLoading: false,
    
    loadMembers: async () => {
      set({ isLoading: true });
      try {
        // Fetch users from Supabase
        const { data, error } = await supabase
          .from('users')
          .select('*, user_credits(*)')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching users:', error);
          throw error;
        }
        
        console.log('Members data from Supabase:', data);
        
        // Convert to Member format
        const users: MemberExtends[] = data.map(member => ({
          id: member.id,
          memberNumber: member.member_number || '',
          email: member.email || '',
          plan: member.plan || 'unsubscribed',
          status: (member.status as MembershipStatus) || 'active',
          registeredAt: member.registered_at || member.created_at,
          lastLoginAt: member.last_login_at || null,
          aiCredits: member.user_credits?.plan_credits || 0,
          maxAiCredits: member.user_credits?.max_plan_credits || 0,
          referralCode: member.referral_code ? decrtyptReferralCode(member.referral_code) : '',
          salesAmount: member.sales_amount || 0,
          scheduledDeletionTime: member.scheduled_deletion_time,
          role: member.role || 'user'
        }));
        
        set({ users, isLoading: false });
      } catch (error) {
        console.error('Failed to load users:', error);
        set({ isLoading: false });
      }
    },
    
    setMembers: (users) => set({ users }),
    
    updateMember: async (id, updates) => {
      try {
        console.log('Updating member:', id, updates);
        // Convert camelCase to snake_case for Supabase
        const snakeCaseUpdates: any = {};
        Object.entries(updates).forEach(([key, value]) => {
          // Convert camelCase to snake_case
          const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          snakeCaseUpdates[snakeKey] = value;
        });
        
        console.log('Snake case updates:', snakeCaseUpdates);
        
        // Update in Supabase
        if(snakeCaseUpdates['ai_credits'] !== undefined) {
          let { error } = await supabase
          .from('user_credits')
          .update({
            plan_credits: snakeCaseUpdates['ai_credits'],
          })
          .eq('user_id', id);

          if (error) throw error;

          delete snakeCaseUpdates['ai_credits'];
        }

        const { error } = await supabase
          .from('users')
          .update(snakeCaseUpdates)
          .eq('id', id);
          
        if (error) throw error;
        
        // Update local state
        set((state) => ({
          users: state.users.map(member =>
            member.id === id ? { ...member, ...updates } : member
          )
        }));
      } catch (error) {
        console.error('Failed to update member:', error);
      }
    },
    
    deleteMember: async (id) => {
      try {
        console.log('Deleting member:', id);
        // Delete from Supabase
        const { error } = await supabase
          .from('users')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        // Update local state
        set((state) => ({
          users: state.users.filter(member => member.id !== id)
        }));
      } catch (error) {
        console.error('Failed to delete member:', error);
      }
    },
    
    cancelDeletion: (id) => set((state) => ({
      users: state.users.map(member =>
        member.id === id ? { ...member, status: 'active', scheduledDeletionTime: undefined } : member
      )
    }))
  };
});